/**
 * Auth Context Helper Functions
 * 
 * Authentication context and utility functions providing:
 * - AuthContext creation
 * - useAuth hook
 * - Auth state management
 * - Auth actions (login, logout, update user)
 * - Permission management
 * 
 * @module contexts/AuthContext.helpers
 */

import React, { createContext, useContext } from 'react';
import { JWT_USER_KEY, JWT_TOKEN_KEY, JWT_REFRESH_KEY } from '../config/api.config.js';

export const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

export const initialState = {
  user: null,
  isAuthenticated: null,  // null = unknown, false = not authenticated, true = authenticated
  isLoading: true,
  error: null,
  permissions: []
};

export const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

export const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        permissions: action.payload.permissions || []
      };
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        permissions: []
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        isAuthenticated: false,  // Explicitly set to false after logout
        isLoading: false
      };
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

export const StorageKeys = {
  JWT_USER_KEY,
  JWT_TOKEN_KEY,
  JWT_REFRESH_KEY
};
